import { IconChevronLeft } from 'central-icons/IconChevronLeft';
import { IconChevronRight } from 'central-icons/IconChevronRight';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { DEFAULT_GRID_ROWS } from '../PageConfigProvider/constants';
import styles from './resize-handle.module.css';

interface ResizeHandleProps {
  onResize: (leftWidth: number) => void;
  initialLeftWidth: number;
  onCollapseLeft: () => void;
  onCollapseRight: () => void;
  isLeftCollapsed: boolean;
  isRightCollapsed: boolean;
}

export const ResizeHandle = ({
  onResize,
  initialLeftWidth: _initialLeftWidth,
  onCollapseLeft,
  onCollapseRight,
  isLeftCollapsed,
  isRightCollapsed,
}: ResizeHandleProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const handleRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLElement | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!(e.target instanceof HTMLElement)) {
      return;
    }

    if (e.target.closest('[data-chevron-button]')) {
      return;
    }
    e.preventDefault();
    setIsDragging(true);

    const container = handleRef.current?.parentElement;
    if (container) {
      containerRef.current = container;
    }
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent): void => {
      if (!isDragging || !containerRef.current) return;

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const newLeftWidth =
        ((e.clientX - containerRect.left) / containerRect.width) * 100;

      const clampedWidth = Math.max(0, Math.min(100, newLeftWidth));
      onResize(clampedWidth);
    },
    [isDragging, onResize]
  );

  const handleMouseUp = useCallback((): void => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    document.body.style.pointerEvents = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const chevronProps = { size: '1.125rem', className: styles.chevron };

  return (
    <div
      ref={handleRef}
      className={styles.handle}
      style={
        {
          '--resize-handle-row': `1 / span ${DEFAULT_GRID_ROWS}`,
        } as CSSProperties
      }
      onMouseDown={handleMouseDown}
    >
      {!isRightCollapsed && (
        <div onClick={onCollapseLeft} data-chevron-button>
          {isLeftCollapsed ? (
            <IconChevronRight {...chevronProps} />
          ) : (
            <IconChevronLeft {...chevronProps} />
          )}
        </div>
      )}
      {!isLeftCollapsed && (
        <div onClick={onCollapseRight} data-chevron-button>
          {isRightCollapsed ? (
            <IconChevronLeft {...chevronProps} />
          ) : (
            <IconChevronRight {...chevronProps} />
          )}
        </div>
      )}
    </div>
  );
};
